/**
 * SubscriptionPortalScreen — l'onglet « Abonnement » de l'espace parent (ports).
 *
 * Un fil : les données, le view-model, les gestes, puis la page. Aucune règle ici — chaque garde
 * du legacy est un booléen du view-model, traduit en « rappel absent = ligne absente ».
 *
 * Les parcours restés legacy (composeur d'activation, Intercom) arrivent en props : c'est
 * l'enveloppe du pont qui les ouvre, parce que `ports/**` ne peut pas importer `src/common/**`.
 *
 * La pause, le cours ponctuel et « Modifier mes créneaux » ont chacun leur feuille dans le design
 * system. Le lien « décalez la séance » de « Modifier mes créneaux » ramène sur l'accueil, qui ouvre
 * « Décaler une séance » : le geste vit là, avec les séances qu'il propose (`triggerMoveSession`, lu
 * par `useParentHomeState`).
 */
import { useNavigation } from "@react-navigation/native"
import React, { useCallback } from "react"

import {
    ConfirmDialog,
    ExtraHoursFlow,
    PauseSubscriptionFlow,
    SlotChangeFlow,
    Snackbar,
    SubscriptionPortalPage,
} from "../../../../../../design-system"
import {
    subscriptionPortalViewModel,
    type PortalSelectedUser,
} from "../../../../view-models/parent/Subscription/subscriptionPortalViewModel"
import { useExtraHoursFlow } from "../../../hooks/useExtraHoursFlow"
import { usePauseSubscriptionFlow } from "../../../hooks/usePauseSubscriptionFlow"
import { usePaymentCollectionDayAdapter } from "../../../hooks/usePaymentCollectionDayAdapter"
import { useAccountTimezoneEntry } from "../../../hooks/useAccountTimezoneEntry"
import { useReferralEntry } from "../../../hooks/useReferralEntry"
import { usePendingSlotChange } from "../../../hooks/usePendingSlotChange"
import { useSlotChangeFlow } from "../../../hooks/useSlotChangeFlow"
import { tutorChangeRequestMessage } from "../../../formatters/slotChangeFormatter"
import { useSubscriptionPortalData } from "./useSubscriptionPortalData"
import { useSubscriptionPortalState } from "./useSubscriptionPortalState"
import { PARENT_TAB_BAR_HEIGHT } from "../ParentTabBar"
import { PARENT_TAB_KEY_TO_ROUTE } from "../parentRoutes"
import { useSelector } from "react-redux"

export type SubscriptionPortalScreenProps = {
    /** L'enfant affiché, tel que `ParentLayoutContext` le porte. */
    selectedUser?: PortalSelectedUser
    /** Ouvre la messagerie du support (`openSupportChat` du contexte de la coquille), un message préparé ou non. */
    onOpenSupport?: (message?: string) => void
}

export function SubscriptionPortalScreen(props: SubscriptionPortalScreenProps) {
    const { selectedUser, onOpenSupport } = props

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const vm = useSelector((state: any) => subscriptionPortalViewModel(state, selectedUser))

    const data = useSubscriptionPortalData({
        selectedUserId: selectedUser?.userId ?? null,
        shouldMarkActivationViewed: vm.shouldMarkActivationViewed,
        studentEmail: vm.studentEmail,
    })

    // Une pause programmée vide `formData.pauseBalance` côté réducteur : sans ce rechargement,
    // la ligne « Annuler la pause » n'apparaîtrait qu'au prochain montage de l'écran.
    const refreshPauseBalance = data.refreshPauseBalance
    const refreshPricing = data.refreshPricing
    const onPauseProgrammed = useCallback(() => {
        refreshPauseBalance()
        refreshPricing()
    }, [refreshPauseBalance, refreshPricing])

    const pause = usePauseSubscriptionFlow({
        childFirstname: vm.studentFirstName,
        onSuccess: onPauseProgrammed,
        onContactSupport: onOpenSupport,
    })
    const openPause = pause.open
    const onPause = useCallback(() => openPause(), [openPause])

    const navigation = useNavigation<{ navigate: (name: string, params?: Record<string, unknown>) => void }>()
    const openMoveSession = useCallback(
        () => navigation.navigate(PARENT_TAB_KEY_TO_ROUTE.parentHome, { triggerMoveSession: true }),
        [navigation]
    )
    // La demande en cours : sur la ligne « Modifier mes créneaux », relue après un changement.
    const pendingChange = usePendingSlotChange(vm.studentId)
    const refreshSchedule = data.refreshSchedule
    const refreshPending = pendingChange.refresh
    const onSlotsChanged = useCallback(() => {
        refreshSchedule()
        refreshPending()
    }, [refreshSchedule, refreshPending])
    const askTutorChange = useCallback(
        () => onOpenSupport?.(tutorChangeRequestMessage(vm.studentFirstName)),
        [onOpenSupport, vm.studentFirstName]
    )
    const slotChange = useSlotChangeFlow({
        childFirstname: vm.studentFirstName,
        onChanged: onSlotsChanged,
        onMoveSession: openMoveSession,
        onAskTutorChange: onOpenSupport != null ? askTutorChange : undefined,
    })

    const portal = useSubscriptionPortalState({
        studentId: vm.studentId,
        activationType: vm.activationType,
        cancelPauseConfirm: vm.cancelPauseConfirm,
        extendConfirm: vm.extendConfirm,
        cancelSubscriptionConfirm: vm.cancelSubscriptionConfirm,
        pauseCancelledMessage: vm.pauseCancelledMessage,
        refreshPricing: data.refreshPricing,
        refreshPauseBalance: data.refreshPauseBalance,
        activationBannerVisible: vm.canActivateNext,
        onTriggerPause: openPause,
        onTriggerSlotChange: slotChange.open,
    })

    // Stable : l'adaptateur garde ce rappel dans la dépendance de son effet de succès.
    const showSnackbar = portal.showSnackbar
    const onCollectionDaySaved = useCallback((_day: number, message: string) => showSnackbar(message), [showSnackbar])

    const extraHours = useExtraHoursFlow({ childFirstname: vm.studentFirstName })
    const referral = useReferralEntry("subscription_portal")
    const timezone = useAccountTimezoneEntry()

    const collectionDay = usePaymentCollectionDayAdapter({
        studentId: vm.studentId,
        email: vm.studentEmail,
        currentDay: vm.paymentCollectionDay,
        studentFirstName: vm.studentFirstName,
        onSuccess: onCollectionDaySaved,
    })

    // Une seule fente d'accusé à l'écran : l'issue d'un parcours fermé pendant son envoi (pause,
    // cours en plus, date de prélèvement) y passe avant les accusés du portail.
    const flowNotice = [pause.notice, extraHours.notice, slotChange.notice, collectionDay.notice].find(
        (notice) => notice.visible
    )

    return (
        <>
            <SubscriptionPortalPage
                loading={vm.loading}
                error={vm.error}
                onRetry={data.retry}
                renewalNotice={vm.renewalNotice}
                onRenewalNotice={vm.renewalNotice != null ? onOpenSupport : undefined}
                offerLabel={vm.offerLabel}
                hoursLabel={vm.hoursLabel}
                statusLabel={vm.statusLabel}
                endLine={vm.endLine}
                objectives={vm.objectives}
                stateLines={vm.stateLines}
                nextStep={vm.nextStep}
                onActivateNext={vm.canActivateNext ? portal.actions.openActivationComposer : undefined}
                onEditNext={vm.canEditNext ? portal.actions.openActivationComposer : undefined}
                onExtend={vm.canExtend ? portal.actions.askExtend : undefined}
                extendLoading={vm.extendLoading}
                onSupport={vm.canSupportNextStep ? onOpenSupport : undefined}
                onChangeSlots={vm.canChangeSlots ? slotChange.open : undefined}
                changeSlotsStatus={pendingChange.pending?.statusLine}
                onPause={vm.canPause ? onPause : undefined}
                onCancelPause={vm.canCancelPause ? portal.actions.askCancelPause : undefined}
                cancelPauseLoading={vm.cancelPauseLoading}
                onExtraLesson={vm.canExtraLesson ? extraHours.open : undefined}
                onRequestChange={vm.canRequestChange ? onOpenSupport : undefined}
                onReferral={referral.onOpenReferral}
                onCollectionDay={vm.canCollectionDay ? collectionDay.open : undefined}
                paymentCollectionDay={vm.paymentCollectionDay}
                collectionDayNote={vm.collectionDayNote}
                onPaymentMethod={vm.canPaymentMethod ? portal.actions.openPaymentMethod : undefined}
                onInvoices={vm.canInvoices ? portal.actions.openInvoices : undefined}
                onCancel={vm.canCancel ? portal.actions.askCancelSubscription : undefined}
                onTimezone={timezone.onOpenTimezone}
                timezoneCity={timezone.timezoneCity}
                collectionDay={vm.canCollectionDay ? collectionDay.flowProps : null}
            />

            <PauseSubscriptionFlow {...pause.flowProps} />
            <ExtraHoursFlow {...extraHours.flowProps} />
            <SlotChangeFlow {...slotChange.flowProps} />

            <ConfirmDialog
                visible={portal.confirm != null}
                title={portal.confirm?.title ?? ""}
                message={portal.confirm?.message}
                confirmLabel={portal.confirm?.confirmLabel}
                cancelLabel={portal.confirm?.cancelLabel}
                danger={portal.confirm?.danger ?? false}
                onConfirm={portal.onConfirm}
                onCancel={portal.onCancelConfirm}
            />

            <Snackbar
                {...(flowNotice ?? {
                    visible: portal.snackbar != null,
                    message: portal.snackbar?.message ?? "",
                    tone: portal.snackbar?.tone,
                    onDismiss: portal.onDismissSnackbar,
                })}
                bottomOffset={PARENT_TAB_BAR_HEIGHT}
            />
        </>
    )
}
